/**
 * Veze između kompozicija — vađenje iz opisa polja.
 *
 * IneryDB nema strane ključeve; `multi_index` ne zna za relacije. Ali MySQL
 * plugin pri uvozu pročita `REFERENTIAL_CONSTRAINTS` i upiše nalaz u opis polja
 * kao ` [FK → tabela.kolona]` (vidi `mysql/schema.js`, `fkNote`). Taj opis ide
 * na lanac u `db.struct`, pa je relacija tu — samo je niko dosad nije čitao.
 *
 * Odatle i granice: veze postoje samo za MySQL uvoz. CSV/JSON/XML dobijaju sam
 * automatski primarni ključ i nemaju šta da prijave.
 */

// Strelica je U+2192 kako je plugin i piše; `->` se prihvata da oznaka
// preživi bilo koje kasnije prepravljanje opisa rukom.
const FK_MARKER = /\[FK\s*(?:→|->)\s*([^.\]]+)\.([^\]]+)\]/;

const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Opis kompozicije ume da pamti izvor na kraju: "... MySQL clinic.patients".
// Ne traži se ključna reč jer je opis pisan i na engleskom i na srpskom, a
// noviji uvozi ga svedu na "Imported from MySQL Import" — tada nema tačke i
// pravilo prosto ne pogađa, što je tačno.
const SOURCE_IN_DESCRIPTION = /(\S+)\.(\S+)\s*$/;

/**
 * FK pokazuje na ime IZVORNE tabele, a kompozicija se može zvati drugačije —
 * `SqlPrescriptions` referiše `patients` dok se kompozicija zove `SqlPatients`.
 * Zato tri pokušaja, od pouzdanog ka slabijem, i odustajanje ako je dvosmisleno.
 *
 * @returns {string|null} ime kompozicije, ili null kad se ne može tvrditi
 */
export const resolveRelationTarget = (table, compositions = []) => {
    if (!table) return null;
    const wanted = normalize(table);

    const exact = compositions.filter((c) => normalize(c.name) === wanted);
    if (exact.length === 1) return exact[0].name;

    const bySource = compositions.filter((c) => {
        const m = SOURCE_IN_DESCRIPTION.exec(c.description || '');
        return m && normalize(m[2]) === wanted;
    });
    if (bySource.length === 1) return bySource[0].name;

    // Poslednje je nagađanje po nastavku imena i sme da prođe samo ako je
    // jedinstveno — radije nikakav link nego link na pogrešnu tabelu.
    const bySuffix = compositions.filter((c) => normalize(c.name).endsWith(wanted));
    if (bySuffix.length === 1) return bySuffix[0].name;

    return null;
};

/**
 * @param {object} field polje iz `db.struct`
 * @param {Array} compositions sve kompozicije baze, za razrešavanje cilja
 * @returns {{table: string, column: string, composition: string|null}|null}
 */
export const relationOf = (field, compositions = []) => {
    const m = FK_MARKER.exec(field?.description || '');
    if (!m) return null;
    const table = m[1].trim();
    return {
        table,
        column: m[2].trim(),
        composition: resolveRelationTarget(table, compositions),
    };
};

/**
 * Opis polja bez FK oznake — oznaka se prikazuje zasebno, pa bi u opisu bila
 * duplirana.
 */
export const descriptionWithoutRelation = (description) =>
    String(description || '').replace(FK_MARKER, '').replace(/\s+$/, '');
